import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { applyActionCode, checkActionCode } from 'firebase/auth'
import { auth } from '@/auth/firebase'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { Button } from '@/components/shared/atoms/Button'
import { AlertCircle } from 'lucide-react'
import { useSendEmailVerification } from '@/hooks/api/useAuth'

// Define the route for handling email verification
export const Route = createFileRoute('/(onboarding)/(auth)/verify-email')({
    validateSearch: (search) => ({
        oobCode:
            typeof search.oobCode === 'string' ? search.oobCode : undefined,
        continueUrl:
            typeof search.continueUrl === 'string'
                ? search.continueUrl
                : undefined,
    }),
    component: VerifyEmailRoute,
})

// Helper function to get user-friendly error messages
function getErrorMessage(errorCode: string): string {
    switch (errorCode) {
        case 'auth/expired-action-code':
            return 'This verification link has expired. Please request a new one.'
        case 'auth/invalid-action-code':
            // Based on Firebase docs: handle invalid or expired codes uniformly
            return 'This verification link is invalid or has expired. Please request a new verification email.'
        case 'auth/user-disabled':
            return 'This account has been disabled. Please contact support.'
        case 'auth/user-not-found':
            return 'No account found with this email address.'
        case 'auth/invalid-continue-uri':
            return 'The continue URL is invalid. Please try again or contact support.'
        case 'auth/missing-continue-uri':
            return 'The continue URL is missing. Please check your link and try again.'
        case 'auth/network-request-failed':
            return 'Network error occurred. Please check your connection and try again.'
        case 'auth/timeout':
            return 'Request timed out. Please try again.'
        case 'auth/too-many-requests':
            return 'Too many verification attempts. Please wait a moment and try again.'
        default:
            return 'Verification failed due to an unexpected error. Please try requesting a new verification email.'
    }
}

function VerifyEmailRoute() {
    const navigate = useNavigate()
    const { oobCode, continueUrl } = useSearch({ from: Route.id })
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
        'loading'
    )
    const [error, setError] = useState<string | null>(null)
    const [errorCode, setErrorCode] = useState<string | null>(null)
    const [emailForVerification, setEmailForVerification] = useState<
        string | null
    >(null)
    const [isResending, setIsResending] = useState(false)
    const [wasAlreadyVerified, setWasAlreadyVerified] = useState(false)
    const { mutate: sendVerification } = useSendEmailVerification()

    useEffect(() => {
        // Sanitize oobCode - remove any tracking parameters or modifications
        let sanitizedOobCode = oobCode
        if (oobCode) {
            // Remove common email tracking parameters and decode if needed
            sanitizedOobCode = oobCode.split('&')[0].split('#')[0]
            try {
                sanitizedOobCode = decodeURIComponent(sanitizedOobCode)
            } catch {
                // If decoding fails, use original
            }
        }

        if (!sanitizedOobCode) {
            setError('Invalid verification link.')
            setStatus('error')
            return
        }

        const verifyEmail = async (retryCount = 0) => {
            try {
                await applyActionCode(auth, sanitizedOobCode)
                // Properly await the user reload to ensure emailVerified is updated
                if (auth.currentUser) {
                    await auth.currentUser.reload()
                }
                setStatus('success')
            } catch (err: any) {
                const code = err.code || 'unknown'

                // Retry logic for network errors
                if (
                    retryCount < 2 &&
                    (code === 'auth/network-request-failed' ||
                        code === 'auth/timeout' ||
                        err.message?.includes('network') ||
                        err.message?.includes('timeout'))
                ) {
                    console.log(
                        `Retrying verification (attempt ${retryCount + 1})`
                    )
                    setTimeout(
                        () => verifyEmail(retryCount + 1),
                        1000 * (retryCount + 1)
                    )
                    return
                }

                setErrorCode(code)

                // Special handling for already used/invalid links
                if (code === 'auth/invalid-action-code' && auth.currentUser) {
                    // Check if the user is already verified
                    await auth.currentUser.reload()
                    if (auth.currentUser.emailVerified) {
                        // User is already verified, show success instead of error
                        setWasAlreadyVerified(true)
                        setStatus('success')
                        return
                    }
                }

                setError(getErrorMessage(code))
                setStatus('error')
            }
        }

        const runPreCheck = async () => {
            try {
                const info = await checkActionCode(auth, sanitizedOobCode)
                // ActionCodeInfo stores email in data.email
                setEmailForVerification(info.data.email || null)
            } catch (err: any) {
                const code = err.code || 'unknown'
                setErrorCode(code)
                setError(getErrorMessage(code))
                setStatus('error')
                return
            }
            verifyEmail()
        }
        runPreCheck()
    }, [oobCode])

    const handleContinue = () => {
        // navigate({ to: continueUrl || '/' });
        navigate({ to: '/' })
    }

    const handleResendVerification = () => {
        if (!auth.currentUser || isResending) return

        setIsResending(true)
        sendVerification(
            { continueUrl: continueUrl || window.location.href },
            {
                onSuccess: () => {
                    setIsResending(false)
                    // Show a success message or navigate to a success state
                    alert(
                        'New verification email sent! Please check your inbox.'
                    )
                },
                onError: (_error) => {
                    setIsResending(false)
                    alert(
                        'Failed to send verification email. Please try again.'
                    )
                },
            }
        )
    }

    const showResendOption =
        errorCode === 'auth/expired-action-code' ||
        errorCode === 'auth/invalid-action-code'

    return (
        <FadeInUp
            delay={0.2}
            className="relative z-10 flex h-full flex-col items-center justify-center px-4"
        >
            {status === 'loading' && (
                <p>Verifying {emailForVerification ?? 'your email'}...</p>
            )}

            {status === 'success' && (
                <div className="text-center">
                    <h1 className="mb-2 text-lg font-semibold">
                        Email{' '}
                        {emailForVerification
                            ? `${emailForVerification} verified!`
                            : 'verified!'}
                    </h1>
                    <p className="mb-4">
                        {emailForVerification
                            ? `Your email ${emailForVerification} has been verified. Thank you!`
                            : 'Thank you for verifying your email address.'}
                    </p>
                    <Button onClick={handleContinue}>
                        {wasAlreadyVerified ? 'Go to the app' : 'Continue'}
                    </Button>
                </div>
            )}

            {status === 'error' && (
                <div className="text-center">
                    <AlertCircle className="mx-auto mb-4" size={56} />
                    <h1 className="mb-2 text-lg font-semibold">
                        Verification failed
                    </h1>
                    <p className="mb-4">{error}</p>
                    <div className="flex flex-col gap-3">
                        {showResendOption && auth.currentUser && (
                            <Button
                                onClick={handleResendVerification}
                                disabled={isResending}
                                variant="solid"
                            >
                                {isResending
                                    ? 'Sending...'
                                    : 'Send new verification email'}
                            </Button>
                        )}
                        {auth.currentUser && (
                            <Button onClick={handleContinue} variant="solid">
                                Continue to App
                            </Button>
                        )}
                        <Button
                            onClick={() => navigate({ to: '/login' })}
                            variant="outline"
                        >
                            Go to Login
                        </Button>
                    </div>
                </div>
            )}
        </FadeInUp>
    )
}
